import SystemConst from "./SystemConst";
import ViewAccessService from "./ViewAccessService";
import INavItem from "../models/INavItem";
import SupplierService from "./SupplierService";
import ISupplierModel from "../models/ISupplierModel";
import IViewAccessDetailFnData from "../models/IViewAccessDetailFnData";
import UserService from "./UserService";
import UserTypesEnum from "../models/UserTypesEnum";
import INavSubItem from "../models/INavSubItem";
import MenuIdEnum from "../models/MenuIdEnum";
import IUserUI from "../models/IUserUI.ts";
import IVendorModel from "../models/IVendorModel.ts";
import SupplierGuidEnum from "../models/SupplierGuidEnum.ts";
import TokenTypesEnum from "../models/TokenTypesEnum.ts";
import IConnection_Token from "../models/IConnection_Token.ts";
import AccessTypesEnum from "../models/AccessTypesEnum";

const getUsersItem = (): INavItem => {
    return {
        menuId: MenuIdEnum.USERS,
        icon: "fad fa-users",
        path: "/users",
        title: "users",
        ariaLabel: "Users",
        exact: true,
        minUserLevel: SystemConst.userTypes.SALES,
        viewAccessDetail: ViewAccessService.Users,
    };
};

const getVendorsItem = (supplier: ISupplierModel, user: IUserUI): INavItem => {
    const vendorTitle = SupplierService.updateStringWithVendorOverride("vendors", supplier);

    const sections: INavSubItem[] = [];

    if (supplier.has_customer_module && user.isSupplierUser && user.supplierUserToken?.user_type_id === UserTypesEnum.ADMIN) {
        sections.push({
            icon: "fad fa-table-rows",
            title: "intake_forms",
            path: "/intake_forms",
            exact: true,
            viewAccessDetail: ViewAccessService.ADMIN_ONLY,
        });
    }

    let path = "/vendors";

    if (supplier.vendor_name_override) {
        path = SupplierService.updateStringWithVendorOverride("/vendors", supplier);
    }

    return {
        menuId: MenuIdEnum.ACCOUNTS,
        icon: "fa-duotone fa-building-user",
        path,
        title: vendorTitle,
        ariaLabel: vendorTitle,
        exact: false,
        minUserLevel: SystemConst.userTypes.SALES,
        viewAccessDetail: ViewAccessService.UsersVendors,
        sections,
    };
};

const getExports = (): INavItem => {
    return {
        menuId: MenuIdEnum.EXPORTS,
        icon: "fad fa-file-export",
        path: "/exports",
        title: "exports",
        ariaLabel: "Exports",
        exact: true,
        minUserLevel: SystemConst.userTypes.MANAGEMENT,
        viewAccessDetail: ViewAccessService.ADMIN_ONLY,
    };
};

const getReports = (): INavItem => {
    return {
        menuId: MenuIdEnum.REPORTS,
        icon: "fad fa-chart-bar",
        path: "/reports",
        title: "reports",
        ariaLabel: "Reports",
        exact: true,
        minUserLevel: SystemConst.userTypes.ADMIN,
        viewAccessDetail: ViewAccessService.Reports,
        viewAccessDetailFn: (): IViewAccessDetailFnData => {
            let useOverride = false;
            let userHasAccess = false;

            const userModel = UserService.getLocalUserDataFn();
            const supplierModel = SupplierService.getLocalSupplierDataFn();

            // console.log("reports viewAccessFn", userModel);

            if (userModel && supplierModel) {
                if (userModel.isSupplierUser && userModel.userType?.id === UserTypesEnum.SALES) {
                    if (!userModel.supplierUserToken?.hide_pricing) {
                        if (supplierModel.has_commissions) {
                            useOverride = true;
                            userHasAccess = true;
                        }
                    }
                }
            }

            return { useOverride, userHasAccess };
        },
    };
};

const getBackorderAnalysis = (): INavSubItem => {
    return {
        icon: "fad fa-truck-loading",
        path: "/inventory/backorders",
        title: "backorders",
        ariaLabel: "Backorders",
        exact: true,
        minUserLevel: SystemConst.userTypes.SHIP_MGMT,
        viewAccessDetail: ViewAccessService.Inventory,
    };
};

const getInventoryWarehouse = (): INavSubItem => {
    return {
        icon: "fad fa-warehouse-alt",
        path: "/inventory/warehouses",
        title: "warehouses",
        ariaLabel: "Warehouses",
        exact: true,
        minUserLevel: SystemConst.userTypes.SALES,
        viewAccessDetail: ViewAccessService.Inventory,
    };
};

const getInventoryWarehouseTransfers = (): INavSubItem => {
    return {
        icon: "fad fa-dolly",
        path: "/inventory/transfers",
        title: "transfers",
        ariaLabel: "Transfers",
        exact: true,
        minUserLevel: SystemConst.userTypes.SALES,
        viewAccessDetail: ViewAccessService.Inventory,
    };
};

const getInventoryLots = (): INavSubItem => {
    return {
        icon: "fad fa-ball-pile",
        path: "/inventory/lots",
        title: "lots",
        ariaLabel: "Lots",
        exact: true,
        minUserLevel: SystemConst.userTypes.SALES,
        viewAccessDetail: ViewAccessService.Inventory,
    };
};

const getInventory = (sections: INavSubItem[]) => {
    return {
        menuId: MenuIdEnum.INVENTORY,
        icon: "fad fa-inventory",
        path: "/inventory",
        title: "inventory",
        ariaLabel: "Inventory",
        exact: true,
        minUserLevel: SystemConst.userTypes.SALES,
        viewAccessDetail: ViewAccessService.Inventory,
        sections,
    };
};

const setRemainingRouteDetails = (
    user: IUserUI,
    supplier: ISupplierModel,
    vendor: IVendorModel,
    vendorUserToken: IConnection_Token | undefined,
    supplierVendorToken: IConnection_Token | undefined
): INavItem[] => {
    const navItems: INavItem[] = [
        {
            menuId: MenuIdEnum.DASHBOARD,
            icon: "fad fa-tachometer-alt-fastest",
            path: "/dashboard",
            title: "dashboard",
            ariaLabel: "Dashboard",
            exact: true,
            minUserLevel: SystemConst.userTypes.SALES,
            viewAccessDetail: ViewAccessService.Dashboard,
            pathFn: () => {
                return getDashboardPath(user, vendorUserToken, supplierVendorToken);
            },
        },
        {
            menuId: MenuIdEnum.ORDERS,
            icon: "fad fa-list-alt",
            path: "/orders",
            title: "orders",
            ariaLabel: "Orders",
            exact: true,
            minUserLevel: SystemConst.userTypes.SALES,
            viewAccessDetail: ViewAccessService.Orders,
        },
        {
            menuId: MenuIdEnum.BUSINESS,
            icon: "fad fa-building",
            path: "/business",
            title: "business",
            ariaLabel: "Business",
            selectable: false,
            exact: true,
            minUserLevel: SystemConst.userTypes.ADMIN,
            viewAccessDetail: ViewAccessService.BusinessProfile,
            sections: [
                {
                    icon: "fad fa-building-magnifying-glass",
                    path: "/business/profile",
                    title: "profile",
                    ariaLabel: "Business Profile",
                    exact: true,
                    viewAccessDetail: ViewAccessService.BusinessProfile,
                },
            ],
        },
    ];

    if (supplier.order_name_override) {
        for (const navItem of navItems) {
            if (navItem.menuId === MenuIdEnum.DASHBOARD) {
                if (supplier.guid === SupplierGuidEnum.PANASONIC_CA) {
                    if (
                        user.vendorUserToken?.dashboard_order_entry ||
                        vendor.Connection_Tokens?.find((token) => token.token_type_id === TokenTypesEnum.SUPPLIER_VENDOR)
                            ?.dashboard_order_entry
                    ) {
                        navItem.title = "new_deal_registration";
                        navItem.ariaLabel = "new_deal_registration";
                    } else {
                        // do nothing?
                    }
                }
            }

            if (navItem.menuId === MenuIdEnum.ORDERS) {
                navItem.title = SupplierService.updateStringWithOrderNameOverride(navItem.title, supplier);
                navItem.path = SupplierService.updateStringWithOrderNameOverride(navItem.path, supplier);

                if (navItem.ariaLabel) {
                    navItem.ariaLabel = SupplierService.updateStringWithOrderNameOverride(navItem.ariaLabel, supplier);
                }

                if (supplier.guid === SupplierGuidEnum.PANASONIC_CA) {
                    navItem.title = "deal_registrations";
                    navItem.ariaLabel = "deal_registrations";
                }
            }
        }
    }

    if (!vendor.is_ecomm_user) {
        const isSupplierUser = user?.isSupplierUser;

        if (user.isVendorSourceUser) {
            navItems.push({
                menuId: MenuIdEnum.PURCHASE_ORDERS,
                icon: "fad fa-th-list",
                path: "/purchase_orders",
                title: "purchase_orders",
                ariaLabel: "Purchase Orders",
                exact: true,
                minUserLevel: UserTypesEnum.SUPPORT,
                viewAccessDetail: ViewAccessService.PurchaseOrders,
            });
        }

        if (isSupplierUser) {
            if (user.userType?.id === UserTypesEnum.ADMIN) {
                navItems[1].sections = [];

                if (supplier.show_batch_features) {
                    // Add Bulk to Orders If An Admin
                    navItems[1].sections = [
                        {
                            icon: "fad fa-conveyor-belt-alt",
                            path: "/orders/batch",
                            title: "batch",
                            ariaLabel: "Batch",
                            exact: true,
                            minUserLevel: SystemConst.userTypes.ADMIN,
                            viewAccessDetail: ViewAccessService.ADMIN_ONLY,
                        },
                    ];
                }

                if (supplier.below_min_price_approval) {
                    navItems[1].sections.push({
                        icon: "fad fa-check",
                        path: "/orders/price_approval",
                        title: "price_approval",
                        ariaLabel: "Price Approval",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.ADMIN_ONLY,
                    });
                }
            }

            if (user.userType?.id === UserTypesEnum.ADMIN) {
                const userItem = MenuService.getUsersItem();

                navItems.push(userItem);
            }

            const vendorItem = MenuService.getVendorsItem(supplier, user);

            navItems.push(vendorItem);

            const catalogs: INavItem = {
                menuId: MenuIdEnum.CATALOGS,
                icon: "fad fa-books",
                path: "/catalogs",
                title: "catalogs",
                ariaLabel: "Catalogs",
                exact: true,
                minUserLevel: SystemConst.userTypes.ADMIN,
                viewAccessDetail: ViewAccessService.Catalogs,
            };

            if (supplier.has_uom_sets) {
                catalogs.sections = [
                    {
                        icon: "fad fa-cart-flatbed-boxes",
                        path: "/uom-sets",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.Catalogs,
                        title: "uom_sets_abbr",
                        ariaLabel: "UoM Sets",
                    },
                ];
            }

            navItems.push(catalogs);

            if (supplier.guid === SupplierGuidEnum.PREFORM) {
                navItems.push({
                    menuId: MenuIdEnum.LABELS,
                    icon: "fad fa-tags",
                    path: "/labels/generator",
                    title: "labels",
                    ariaLabel: "Labels",
                    exact: true,
                    minUserLevel: SystemConst.userTypes.SALES,
                    viewAccessDetail: ViewAccessService.Orders,
                });
            }

            // if (supplier.has_client_module) {
            //     navItems.push({
            //         icon: "fad fa-user",
            //         path: "/clients",
            //         title: "Clients",
            //         exact: true,
            //         minUserLevel: SystemConst.userTypes.SALES,
            //     });
            // }

            if (supplier.has_purchase_orders) {
                if (user.userType?.id === UserTypesEnum.ADMIN || user.userType?.id === UserTypesEnum.INVENTORY_MGMT) {
                    const sections: INavSubItem[] = [
                        {
                            icon: "fad fa-handshake",
                            title: "vendor_sources",
                            path: "/vendor_sources",
                            ariaLabel: "Vendor Sources",
                            exact: true,
                            minUserLevel: UserTypesEnum.ADMIN,
                            viewAccessDetail: ViewAccessService.PurchaseOrders,
                        },
                    ];

                    navItems.push({
                        menuId: MenuIdEnum.PURCHASE_ORDERS,
                        icon: "fad fa-th-list",
                        path: "/purchase_orders",
                        title: "purchase_orders",
                        ariaLabel: "Purchase Orders",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.INVENTORY_MGMT,
                        viewAccessDetail: ViewAccessService.PurchaseOrders,
                        sections,
                    });
                }
            }

            if (supplier.has_discounts) {
                if (user.userType?.id === UserTypesEnum.ADMIN) {
                    navItems.push({
                        menuId: MenuIdEnum.DISCOUNTS,
                        icon: "fad fa-dollar",
                        path: "/discounts",
                        title: "discounts",
                        ariaLabel: "Discounts",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.ADMIN_ONLY,
                    });
                }
            }

            if (supplier.has_bom) {
                if (user.userType?.id === UserTypesEnum.ADMIN) {
                    navItems.push({
                        menuId: MenuIdEnum.MATERIALS,
                        icon: "fad fa-cubes",
                        path: "/materials",
                        title: "materials",
                        ariaLabel: "Materials",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.Materials,
                    });
                }
            }

            if (supplier.has_rep_groups) {
                if (user.userType?.id === UserTypesEnum.ADMIN) {
                    navItems.push({
                        menuId: MenuIdEnum.REP_GROUPS,
                        icon: "fad fa-users-class",
                        path: "/rep-groups",
                        title: "rep_groups",
                        ariaLabel: "Rep Groups",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.Materials,
                    });
                }
            }

            if (supplier.has_routes) {
                if (user.userType?.id === UserTypesEnum.ADMIN) {
                    navItems.push({
                        menuId: MenuIdEnum.ROUTES,
                        icon: "fad fa-road",
                        path: "/routes",
                        title: "routes",
                        ariaLabel: "Routes",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.Materials,
                        sections: [
                            {
                                icon: "fad fa-truck-front",
                                path: "/routes/daily",
                                title: "daily_routes",
                                ariaLabel: "Daily Routes",
                                exact: true,
                                minUserLevel: SystemConst.userTypes.SUPPORT,
                                viewAccessDetail: ViewAccessService.Orders,
                            },
                        ],
                    });
                }
            }

            if (supplier.has_pick_list_management) {
                if (
                    user.userType?.id === UserTypesEnum.ADMIN ||
                    user.userType?.id === UserTypesEnum.INVENTORY_MGMT ||
                    user.userType?.id === UserTypesEnum.ORDER_MGMT
                ) {
                    navItems.push({
                        menuId: MenuIdEnum.PICK_LISTS,
                        icon: "fad fa-hand-holding-box",
                        path: "/pick_lists",
                        title: "pick_lists",
                        ariaLabel: "Pick Lists",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.Inventory,
                    });

                    navItems.push({
                        menuId: MenuIdEnum.PACKING_LISTS,
                        icon: "fad fa-box-check",
                        path: "/packing_lists",
                        title: "packing_lists",
                        ariaLabel: "Packing Lists",
                        exact: true,
                        minUserLevel: SystemConst.userTypes.ADMIN,
                        viewAccessDetail: ViewAccessService.Inventory,
                    });
                }
            }

            // if (supplier.has_product_sources) {
            //     if (user.userType?.id === UserTypesEnum.ADMIN) {
            //         navItems.push({
            //             icon: "fad fa-sitemap",
            //             path: "/sources",
            //             title: "Sources",
            //             exact: true,
            //             minUserLevel: SystemConst.userTypes.ADMIN,
            //             viewAccessDetail: ViewAccessService.Sources,
            //         });
            //     }
            // }

            for (const navItem of navItems) {
                if (navItem.title === "business" && navItem.sections) {
                    navItem.sections.push(
                        {
                            icon: "fad fa-exchange-alt",
                            path: "/business/integrations",
                            title: "integrations",
                            ariaLabel: "Business Integrations",
                            exact: true,
                            minUserLevel: SystemConst.userTypes.ADMIN,
                            viewAccessDetail: ViewAccessService.BusinessIntegrations,
                        },
                        {
                            icon: "fad fa-cog",
                            path: "/business/settings",
                            title: "settings",
                            ariaLabel: "Business Settings",
                            exact: true,
                            minUserLevel: SystemConst.userTypes.ADMIN,
                            viewAccessDetail: ViewAccessService.BusinessSettings,
                        }
                    );

                    if (supplier) {
                        if (supplier.has_rebates) {
                            navItem.sections.push({
                                icon: "fad fa-money-check-alt",
                                path: "/business/rebates",
                                title: "rebates",
                                ariaLabel: "Business Rebates",
                                exact: true,
                                minUserLevel: SystemConst.userTypes.ADMIN,
                                viewAccessDetail: ViewAccessService.BusinessSettings,
                            });
                        }

                        if (supplier.has_rebates) {
                            navItem.sections.push({
                                icon: "fad fa-sack-dollar",
                                path: "/business/commissions",
                                title: "commissions",
                                ariaLabel: "Business Commissions",
                                exact: true,
                                minUserLevel: SystemConst.userTypes.ADMIN,
                                viewAccessDetail: ViewAccessService.BusinessSettings,
                            });
                        }

                        // if (supplier.has_coop_advertisement) {
                        //     route.sections.push({
                        //         icon: "fad fa-handshake",
                        //         path: "/business/coop",
                        //         title: "Coop",
                        //         exact: true,
                        //         minUserLevel: SystemConst.userTypes.ADMIN,
                        //     });
                        // }

                        if (supplier.has_spiff) {
                            navItem.sections.push({
                                icon: "fad fa-funnel-dollar",
                                path: "/business/spiff",
                                title: "spiff",
                                ariaLabel: "Business SPIFF",
                                exact: true,
                                minUserLevel: SystemConst.userTypes.ADMIN,
                                viewAccessDetail: ViewAccessService.BusinessSettings,
                            });
                        }
                    }
                }
            }

            if (user.supplierUserToken?.access_type_id !== AccessTypesEnum.READ_ONLY) {
                const reportsRouteDetail = MenuService.getReports();

                navItems.splice(2, 0, reportsRouteDetail);

                if (supplier.has_exports) {
                    const exportsRoute = MenuService.getExports();

                    navItems.push(exportsRoute);
                }
            }
        }

        let vendorRouteIndex = 0;

        for (let i = 0; i < navItems.length; ++i) {
            if (navItems[i].path === "/vendors") {
                vendorRouteIndex = i;
                break;
            }
        }

        if (user.guid === "9adde305-d36c-4bde-bc55-4b76da2066ad" || supplier.has_client_module) {
            let start = 2;

            if (supplier.has_quotes) {
                start++;
            }

            navItems.splice(vendorRouteIndex + 1, 0, {
                menuId: MenuIdEnum.CLIENTS,
                icon: "fad fa-user-tie",
                path: "/clients",
                title: "clients",
                ariaLabel: "Clients",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Clients,
            });
        }

        if (supplier.has_shipments) {
            const shipmentRouteDetail: INavItem = {
                menuId: MenuIdEnum.SHIPMENTS,
                icon: "fad fa-truck",
                path: "/shipments",
                title: "shipments",
                ariaLabel: "Shipments",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Shipments,
                sections: [],
            };

            if (user.userType?.id === UserTypesEnum.ADMIN && user?.isSupplierUser && supplier.show_batch_features) {
                shipmentRouteDetail.sections!.push({
                    icon: "fad fa-boxes",
                    path: "/shipments/batch",
                    title: "batch",
                    ariaLabel: "Batch",
                    exact: true,
                    minUserLevel: SystemConst.userTypes.ADMIN,
                    viewAccessDetail: ViewAccessService.ADMIN_ONLY,
                });
            }

            navItems.splice(2, 0, shipmentRouteDetail);
        }

        if (supplier.has_projects) {
            const projectsRouteDetail = {
                menuId: MenuIdEnum.PROJECTS,
                icon: "fad fa-project-diagram",
                path: "/projects",
                title: "projects",
                ariaLabel: "Projects",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Projects,
            };

            navItems.splice(2, 0, projectsRouteDetail);
        }

        if (supplier.has_templates) {
            const quotesRouteDetail = {
                menuId: MenuIdEnum.TEMPLATES,
                icon: "fad fa-compass-drafting",
                path: "/templates",
                title: "templates",
                ariaLabel: "Templates",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Templates,
            };

            navItems.splice(1, 0, quotesRouteDetail);
        }

        if (supplier.has_quotes) {
            const quotesRouteDetail = {
                menuId: MenuIdEnum.QUOTES,
                icon: "fad fa-file-alt",
                path: "/quotes",
                title: "quotes",
                ariaLabel: "Quotes",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Quotes,
            };

            navItems.splice(1, 0, quotesRouteDetail);
        }

        if (user?.isSupplierUser && supplier.has_take_offs) {
            const takeOffsDetail = {
                menuId: MenuIdEnum.TAKE_OFFS,
                icon: "fad fa-table",
                path: "/take_offs",
                title: "take_offs",
                ariaLabel: "Take Offs",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Quotes,
            };

            navItems.splice(1, 0, takeOffsDetail);
        }

        // if (isSupplierUser) {
        let showInventorySection = true;

        // if (supplier.hide_inventory_from_vendors && !user?.isSupplierUser) {
        //     showInventorySection = false;
        // }

        if (!supplier.has_inventory) {
            showInventorySection = false;
        }

        if (isSupplierUser) {
            if (supplier.hide_inventory_from_sales_reps) {
                if (user?.userType?.id === UserTypesEnum.SALES || user?.supplierUserToken?.is_sales_rep) {
                    showInventorySection = false;
                }
            }
        } else {
            if (supplier.hide_inventory_from_vendors) {
                // TODO - Move this into a setting
                if (supplier.guid === SupplierGuidEnum.SYNTHAX) {
                    showInventorySection = true;
                } else {
                    showInventorySection = false;
                }
            }
        }

        if (showInventorySection) {
            const inventorySections: INavSubItem[] = [];

            if (supplier.has_warehouses && user?.isSupplierUser && user.userType?.id === UserTypesEnum.ADMIN) {
                const inventoryWarehouseRoute = MenuService.getInventoryWarehouse();

                inventorySections.push(inventoryWarehouseRoute);

                const inventoryWarehouseTransfersRoute = MenuService.getInventoryWarehouseTransfers();

                inventorySections.push(inventoryWarehouseTransfersRoute);
            }

            if (supplier.has_lots && user?.isSupplierUser) {
                const inventoryLotsRoute = MenuService.getInventoryLots();

                inventorySections.push(inventoryLotsRoute);
            }

            if (user?.isSupplierUser && user.userType?.id === UserTypesEnum.ADMIN && supplier.has_backorders) {
                const backorderAnalysisRoute = MenuService.getBackorderAnalysis();

                inventorySections.push(backorderAnalysisRoute);
            }

            const inventoryRouteDetail = MenuService.getInventory(inventorySections);

            navItems.splice(navItems.length - 1, 0, inventoryRouteDetail);
        }

        if (supplier.has_automation) {
            // navItems.push({
            //     icon: "fad fa-conveyor-belt-alt",
            //     path: "/automation",
            //     title: "Automation",
            //     exact: true,
            //     minUserLevel: SystemConst.userTypes.ADMIN,
            // });
        }
        // }

        if ((supplier.has_accounting || supplier.Supplier_Integration?.qbo_grouped_invoices) && isSupplierUser) {
            navItems.push({
                menuId: MenuIdEnum.ACCOUNTING,
                icon: "fad fa-abacus",
                path: "/accounting",
                title: "accounting",
                ariaLabel: "Accounting",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Accounting,
            });
        }

        // @deprecated
        // if (supplier.has_forecast) {
        //     navItems.push({
        //         menuId: MenuIdEnum.FORECAST,
        //         icon: "fad fa-chart-line",
        //         path: "/forecast",
        //         title: "forecast",
        //         ariaLabel: "Forecast",
        //         exact: true,
        //         minUserLevel: SystemConst.userTypes.SALES,
        //         viewAccessDetail: ViewAccessService.Forecasts,
        //     });
        // }

        // if (user?.isSupplierUser) {
        //     navItems.push({
        //         menuId: MenuIdEnum.CACHE,
        //         icon: "fad fa-database",
        //         path: "/cache",
        //         title: "cache",
        //         ariaLabel: "Cache",
        //         exact: true,
        //         minUserLevel: SystemConst.userTypes.SALES,
        //         viewAccessDetail: ViewAccessService.Caches,
        //     });
        //
        //     navItems.push({
        //         menuId: MenuIdEnum.SYNC,
        //         icon: "fad fa-sync",
        //         path: "/sync",
        //         title: "sync",
        //         ariaLabel: "Sync",
        //         exact: true,
        //         minUserLevel: SystemConst.userTypes.SALES,
        //         viewAccessDetail: ViewAccessService.Sync,
        //     });
        // }
    }

    return navItems;
};

const setInitialRoutes = (userModel: IUserUI, vendorUserToken: IConnection_Token, supplierVendorToken: IConnection_Token) => {
    const navItems: INavItem[] = [
        {
            menuId: MenuIdEnum.DASHBOARD,
            icon: "fad fa-tachometer-alt-fastest",
            path: "/dashboard",
            title: "dashboard",
            ariaLabel: "Dashboard",
            exact: true,
            minUserLevel: SystemConst.userTypes.SALES,
            viewAccessDetail: ViewAccessService.Dashboard,
            pathFn: () => {
                return getDashboardPath(userModel, vendorUserToken, supplierVendorToken);
            },
        },
        {
            menuId: MenuIdEnum.ORDERS,
            icon: "fad fa-list-alt",
            path: "/orders",
            title: "orders",
            ariaLabel: "Orders",
            exact: true,
            minUserLevel: SystemConst.userTypes.SALES,
            viewAccessDetail: ViewAccessService.Orders,
        },
        {
            menuId: MenuIdEnum.BUSINESS,
            icon: "fad fa-building",
            path: "/business",
            title: "business",
            ariaLabel: "Business",
            selectable: false,
            exact: true,
            minUserLevel: SystemConst.userTypes.ADMIN,
            viewAccessDetail: ViewAccessService.BusinessProfile,
            sections: [
                {
                    icon: "fad fa-building-magnifying-glass",
                    path: "/business/profile",
                    title: "profile",
                    ariaLabel: "Business Profile",
                    exact: true,
                    viewAccessDetail: ViewAccessService.BusinessProfile,
                },
            ],
        },
    ];

    return navItems;
};

const getDashboardPath = (
    userModel: IUserUI,
    vendorUserToken: IConnection_Token | undefined,
    supplierVendorToken: IConnection_Token | undefined
) => {
    if (
        userModel?.supplierUserToken?.dashboard_order_entry ||
        vendorUserToken?.dashboard_order_entry ||
        supplierVendorToken?.dashboard_order_entry
    ) {
        return "/dashboard/ordering";
    } else {
        return "/dashboard";
    }
};

const getEcommRoutes = (supplierModel: ISupplierModel) => {
    const navItems: INavItem[] = [
        {
            menuId: MenuIdEnum.HOME,
            icon: "fad fa-home",
            path: "/ecomm",
            title: "home",
            ariaLabel: "Home",
            exact: true,
            minUserLevel: SystemConst.userTypes.SALES,
            viewAccessDetail: ViewAccessService.Dashboard,
        },
        {
            menuId: MenuIdEnum.ORDERS,
            icon: "fad fa-list-alt",
            path: "/ecomm/orders",
            title: "orders",
            ariaLabel: "Orders",
            exact: true,
            minUserLevel: SystemConst.userTypes.SALES,
            viewAccessDetail: ViewAccessService.Orders,
        },
    ];

    if (supplierModel) {
        if (supplierModel.has_shipments) {
            navItems.push({
                menuId: MenuIdEnum.SHIPMENTS,
                icon: "fad fa-box",
                path: "/ecomm/shipments",
                title: "shipments",
                ariaLabel: "Shipments",
                exact: true,
                minUserLevel: SystemConst.userTypes.SALES,
                viewAccessDetail: ViewAccessService.Shipments,
            });
        }
    }

    return navItems;
};

const MenuService = {
    getUsersItem,
    getVendorsItem,
    getExports,
    getReports,
    getBackorderAnalysis,
    getInventoryWarehouse,
    getInventoryWarehouseTransfers,
    getInventoryLots,
    getInventory,
    setRemainingRouteDetails,
    setInitialRoutes,
    getDashboardPath,
    getEcommRoutes,
};

export default MenuService;
